import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import styled from 'styled-components/macro';

import Page, { PageButton } from '../../../components/Page';
import Card from '../../../components/Card';
import { namespace } from '../actions';
import { LoadingOverlay } from '../../../components/Loading';
import ConfigurationsTable from './ConfigurationsTable';
import NoData from './NoData';
import Icon from '../../../components/Icons';
import { useActions } from '../../../Actions';
import useEffectCallback from '../../../util/useEffectCallback';
import { useAccountIds } from '../../../auth/accessToken/withAccountIds';
import { distances } from '../../../styles/constants';
import DeleteConfigurationModal from './DeleteConfigurationModal';
import CreateConfigurationModal from './CreateConfigurationModal';
import { H1 } from '../../../components/Typography';
import { State } from '../../../reducer';
import usePayoutDestinations from '../../../util/usePayoutDestinations';

const configurationListSelector = (state: State) => state[namespace];

const reportListSelector = createSelector(
    configurationListSelector,
    (configurationState) => configurationState
);

const ReportList = () => {
    const {
        fetchConfigurations,
        openDeleteModal,
        closeDeleteModal,
        removeConfiguration,
        openNewModal,
        closeNewModal,
        postNewConfiguration,
    } = useActions(namespace);
    const { accountIds } = useAccountIds();
    const { t } = useTranslation();
    const {
        configurations,
        isLoading,
        configurationToDelete,
        account_id,
        showNewConfigurationModal,
        createError,
    } = useSelector(reportListSelector);
    useEffectCallback(
        (accountIds: string[]) => {
            fetchConfigurations(accountIds);
        },
        [accountIds]
    );
    const prodAccount = accountIds.find((x) => x.startsWith('P'));
    const hasProdConfigurations = prodAccount && (configurations[prodAccount] || []).length > 0;

    const testAccount = accountIds.find((x) => x.startsWith('T'));
    const hasTestConfigurations = testAccount && (configurations[testAccount] || []).length > 0;

    const { destinationOptions, destinationIdLabelMap } = usePayoutDestinations(accountIds);

    return (
        <>
            <Page
                title={
                    <>
                        <Title>{t('settings.reports.title')}</Title>
                        <PageButton onClick={openNewModal}>
                            <Icon icon="document" />
                            {t('settings.reports.create_new_report')}
                        </PageButton>
                    </>
                }
            >
                {hasProdConfigurations && (
                    <Card title={t('environments.production')} noPadding marginBottom={distances.small}>
                        <ConfigurationsTable
                            accountId={prodAccount}
                            configurations={configurations[prodAccount] || []}
                            openDeleteModal={openDeleteModal}
                            destinationIdLabelMap={destinationIdLabelMap[prodAccount]}
                        />
                    </Card>
                )}
                {hasTestConfigurations && (
                    <Card title={t('environments.test')} noPadding marginBottom={distances.small}>
                        <ConfigurationsTable
                            accountId={testAccount}
                            configurations={configurations[testAccount] || []}
                            openDeleteModal={openDeleteModal}
                            destinationIdLabelMap={destinationIdLabelMap[testAccount]}
                        />
                    </Card>
                )}
                {isLoading && (
                    <Card marginBottom={distances.small}>
                        <LoadingOverlay />
                    </Card>
                )}
                {!isLoading && !hasProdConfigurations && !hasTestConfigurations && (
                    <Card marginBottom={distances.small}>
                        <NoData openNewModal={openNewModal} />
                    </Card>
                )}
            </Page>
            {account_id && configurationToDelete && (
                <DeleteConfigurationModal
                    accountId={account_id}
                    configuration={configurationToDelete}
                    removeConfiguration={removeConfiguration}
                    dismissModal={closeDeleteModal}
                />
            )}
            {showNewConfigurationModal && (
                <CreateConfigurationModal
                    destinations={destinationOptions}
                    dismissModal={closeNewModal}
                    accountIds={accountIds}
                    createConfiguration={postNewConfiguration}
                    createError={createError}
                />
            )}
        </>
    );
};
const Title = styled(H1 as any)`
    display: inline-block;
`;

export default ReportList;
